import { FormTextField } from '@schooly/components/form-text-field';
import { FC } from 'react';
import { Controller, Path, useFormContext } from 'react-hook-form-lts';
import { useIntl } from 'react-intl';

import { AttendanceFormEntries } from './utils';

export type AttendanceSidebarInternalNoteInputProps = {
  name: Path<AttendanceFormEntries>;
  disabled?: boolean;
  autoFocus?: boolean;
  onEmptyBlur?: () => void;
};

export const AttendanceSidebarInternalNoteInput: FC<AttendanceSidebarInternalNoteInputProps> = ({
  name,
  disabled,
  autoFocus,
  onEmptyBlur,
}) => {
  const { formatMessage } = useIntl();
  const { control } = useFormContext<AttendanceFormEntries>();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange, onBlur, ...field } }) => (
        <FormTextField
          {...field}
          value={typeof value === 'string' ? value : ''}
          disabled={disabled}
          autoFocus={autoFocus}
          fullWidth
          multiline
          minRows={1}
          maxRows={9}
          showEndAdornment={false}
          label={formatMessage({ id: 'attendance-InternalNote' })}
          placeholder={formatMessage({ id: 'attendance-YourInternalNote' })}
          inputProps={{ onKeyDown: (e) => e.stopPropagation() }}
          onChange={(e) => onChange(e.target.value)}
          onBlur={(e) => {
            onBlur();
            if (!e.target.value.trim()) {
              onChange(null);
              onEmptyBlur?.();
            }
          }}
          sx={(theme) => ({
            '&.MuiTextField-root .MuiInputLabel-root': {
              textTransform: 'uppercase',
              '&.Mui-focused, &.MuiFormLabel-filled': {
                ...theme.typography.h4,
                color: theme.palette.text.secondary,
                transform: `translate(${theme.spacing(1.25)}, ${theme.spacing(1.25)})`,
              },
            },

            '& .MuiOutlinedInput-root': {
              margin: 0,
              padding: theme.spacing(1.25),
              paddingTop: theme.spacing(3.75),
              border: `1px solid ${theme.palette.common.light2}`,

              '&.Mui-focused, &:hover': {
                borderColor: theme.palette.common.light2,
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              },

              '&.Mui-disabled': {
                '&:hover': { borderColor: `${theme.palette.common.light2} !important` },
                '& .MuiOutlinedInput-notchedOutline': {
                  backgroundColor: 'transparent',
                },
                '& .MuiOutlinedInput-input': {
                  color: theme.palette.primary.main,
                  WebkitTextFillColor: theme.palette.primary.main,
                },
              },

              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },

              '& .MuiOutlinedInput-input': {
                ...theme.typography.body1,
                padding: 0,
                '&::placeholder': { opacity: 1, color: theme.palette.text.secondary },
              },
            },
          })}
        />
      )}
    />
  );
};
